import { isPresent } from '@luminovo/commons';
import {
    FullPart,
    isCustomComponentFull,
    isCustomFullPart,
    isGenericFullPart,
    isOtsComponentFull,
    isOtsFullPart,
    StandardPartTypes,
    StockInformationDTO,
} from '@luminovo/http-client';
import { BomItem } from '../../resources/designItem/bomItemFrontendTypes';
import { useHttpQuery } from '../../resources/http/useHttpQuery';
import { calculateRequiredStock } from '../../resources/sourcingScenario/calculateRequiredStock';
import { useSourcingScenariosOfRfq } from '../../resources/sourcingScenario/sourcingScenarioHandlers';
import { assertUnreachable } from '../../utils/typingUtils';

type AvailabilityType = 'inStock' | 'outOfStock' | 'insufficientStock';

export interface UseStockAvailabilityInfoResult {
    type: AvailabilityType;
    stock: StockInformationDTO;
    maxRequiredStock: number;
}

export function usePartAvailability({
    rfqId,
    assemblyId,
    bomItem,
    part,
}: {
    rfqId?: string;
    assemblyId: string;
    bomItem: BomItem;
    part: FullPart;
}): UseStockAvailabilityInfoResult | null {
    const { data: sourcingScenarios = [] } = useSourcingScenariosOfRfq(rfqId);
    const { minRequiredStock, maxRequiredStock } = calculateRequiredStock({ sourcingScenarios, bomItem });
    const { data: partAvailability } = useFetchInnerPartAvailability({ part, assemblyId });

    const stock = partAvailability?.stock;

    if (!isPresent(stock)) {
        return null;
    }

    const availableStock: number = stock.external.quantity?.quantity ?? 0;
    const availableInventory: number = stock.inventory.quantity?.quantity ?? 0;
    const maxAvailableStock: number = Math.max(availableStock, availableInventory);

    const type = (): AvailabilityType => {
        if (maxRequiredStock < maxAvailableStock) {
            return 'inStock';
        }
        if (minRequiredStock > maxAvailableStock) {
            return 'outOfStock';
        }
        return 'insufficientStock';
    };

    return {
        stock,
        type: type(),
        maxRequiredStock,
    };
}

function getPartType(part: FullPart): StandardPartTypes | undefined {
    if (isGenericFullPart(part)) {
        return StandardPartTypes.Generic;
    }
    if (isOtsFullPart(part)) {
        return StandardPartTypes.OffTheShelf;
    }
    if (isOtsComponentFull(part)) {
        return StandardPartTypes.Ipn;
    }
    if (isCustomFullPart(part) || isCustomComponentFull(part)) {
        // custom parts are not "standard".
        return undefined;
    }
    assertUnreachable(part);
}

function useFetchInnerPartAvailability({ part, assemblyId }: { part: FullPart; assemblyId: string }) {
    const partType = getPartType(part);

    return useHttpQuery(
        'POST /parts/availability',
        {
            requestBody: { assembly_id: assemblyId, ids: [{ type: partType!, data: part.id }] },
        },
        {
            select: (res) => res.items[0],
            enabled: !!partType,
            refetchInterval({ state }) {
                if (state.data === undefined || state.data.items[0].stock === null) {
                    return 5_000;
                }
                if (!state.data.items[0].stock.external.last_updated) {
                    return 5_000;
                }

                return false;
            },
        },
    );
}
